/**
 * Tempo relativo em pt-BR para o Início do portal ("agora", "há 5 min", "há 3 h",
 * "ontem", "há 4 dias", depois a data).
 *
 * Por que um helper próprio: o sino (`PortalNotificationBell`) mostra hora
 * ABSOLUTA ("22/06 12:00"), e o único texto relativo que o portal já tem vive
 * como função local, não exportada, em `pages/portal/AtendimentoPage.tsx`. A
 * redação daqui segue a dele ("há N min", "há N h") para o parceiro ler a mesma
 * língua em toda tela do portal. Sem biblioteca nova: `date-fns` já é dependência.
 *
 * `agora` é injetável só para teste determinístico; em produção fica o
 * `Date.now()` padrão.
 */
import { format, parseISO } from "date-fns";

const MINUTO = 60_000;
const HORA = 60 * MINUTO;
const DIA = 24 * HORA;

function inicioDoDia(instante: number): number {
  const d = new Date(instante);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

/** Dias de calendário entre a data e "agora" (`round` absorve a hora de verão). */
function diasDeCalendario(data: Date, agora: number): number {
  return Math.round((inicioDoDia(agora) - inicioDoDia(data.getTime())) / DIA);
}

function lerData(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const data = parseISO(iso);
  return Number.isNaN(data.getTime()) ? null : data;
}

export function tempoRelativo(iso: string | null | undefined, agora: number = Date.now()): string {
  const data = lerData(iso);
  if (!data) return "";

  const diferenca = agora - data.getTime();
  // Relógio do servidor à frente do navegador cai aqui: "agora", nunca "há -2 min".
  if (diferenca < MINUTO) return "agora";
  if (diferenca < HORA) return `há ${Math.floor(diferenca / MINUTO)} min`;
  if (diferenca < DIA) return `há ${Math.floor(diferenca / HORA)} h`;

  // A partir de 24 h conta-se por calendário, não por blocos de 24 h: às 12h
  // de quinta, terça às 13h tem 47 h e é "há 2 dias" — não "ontem" nem "há 1 dias".
  const dias = diasDeCalendario(data, agora);
  if (dias <= 1) return "ontem";
  if (dias < 7) return `há ${dias} dias`;

  return format(data, "dd/MM/yyyy");
}

/** Data e hora completas, para o `title` do `<time>` e para as datas do comunicado. */
export function dataHoraCompleta(iso: string | null | undefined): string {
  const data = lerData(iso);
  if (!data) return "";
  return format(data, "dd/MM/yyyy 'às' HH:mm");
}
